import { ROUTES } from '#/config/routes';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import SnProductMappingStatusBadge from '#/modules/crmMgmt/aftersales/snProductMapping/components/SnProductMappingStatusBadge';
import { SN_PRODUCT_MAPPING_STATUS_FILTER_OPTIONS } from '#/modules/crmMgmt/aftersales/snProductMapping/helper/snProductMappingStatus';
import { searchCustomerFilterOptions } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { formatDateOrDash } from '#/utils/date';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

/** list record 為 `YYYY-MM-DD HH:mm:ss`；取日部分顯示。 */
function formatListDate(value) {
  if (value == null || value === '') {
    return '—';
  }
  return formatDateOrDash(String(value).trim().slice(0, 10));
}

function formatCustomerLabel(customer) {
  if (!customer) {
    return '未指定';
  }
  return [customer.code, customer.name].filter(Boolean).join(' - ') || '—';
}

// BE sort 白名單：product_code、product_name、status、updated_at
export function buildSnProductMappingColumns({
  filters = {},
  onFilterChange,
  customerLabelCache,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.snProductMappingDetail(row.id),
      getEditPath: (row) => ROUTES.snProductMappingEdit(row.id),
      canShowEdit: () => true,
      size: 100,
      minSize: 100,
    }),
    {
      id: 'productCode',
      header: '產品代碼',
      accessorFn: (row) => displayDash(row.productCode),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'productCode',
        label: '產品代碼',
        filterValue: filters.productCode,
        onChange: (value) => onFilterChange('productCode', value),
        placeholder: '搜尋產品代碼',
      }),
    },
    {
      id: 'productName',
      header: '產品名稱',
      accessorFn: (row) => displayDash(row.productName),
      size: 220,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'productName',
          label: '產品名稱',
          filterValue: filters.productName,
          onChange: (value) => onFilterChange('productName', value),
          placeholder: '搜尋產品名稱',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 120,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: SN_PRODUCT_MAPPING_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
      cell: ({ row }) => (
        row.original.status
          ? <SnProductMappingStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
    },
    {
      id: 'customer',
      header: '客戶',
      accessorFn: (row) => formatCustomerLabel(row.customer),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'customerId',
          label: '客戶',
          searchOptions: searchCustomerFilterOptions,
          filterValue: filters.customerId,
          onChange: (value) => onFilterChange('customerId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(customerLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(customerLabelCache, options, values);
          },
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'createdAt',
      header: '建立日期',
      accessorFn: (row) => formatListDate(row.record?.createdAt),
      size: 120,
      minSize: 120,
      enableSorting: false,
    },
    {
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayDash(row.remark),
      size: 220,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
  ];
}
